import React, { useState, useMemo, useCallback, memo } from "react";
import { Form, Button } from "react-bootstrap";
import { YearSelector } from "./components/shared/YearSelector";
import { useEconomicNexus } from "./hooks/useEconomicNexus";
import { economicNexusApi } from "./api/economicNexusApi";
import { toast } from "react-toastify";
import { DocumentDownload } from "iconsax-react";
import styles from "./EconomyNexus.module.scss";

const QUARTERS = [
  { id: 0, name: "Year" },
  { id: 1, name: "Qrt 1" },
  { id: 2, name: "Qrt 2" },
  { id: 3, name: "Qrt 3" },
  { id: 4, name: "Qrt 4" },
];

const EconomyNexus = memo(() => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(QUARTERS[0].id);
  const [exportLoading, setExportLoading] = useState(false);

  const { data: nexusData, isLoading, error } = useEconomicNexus(year, quarter);

  // Memoize options rendering
  const quarterOptions = useMemo(
    () =>
      QUARTERS.map((data) => (
        <option value={data.id} key={data.id}>
          {data.name}
        </option>
      )),
    [],
  );

  const handleYearChange = useCallback((newYear) => {
    setYear(newYear);
  }, []);

  const handleQuarterChange = useCallback((e) => {
    setQuarter(Number(e.target.value));
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setExportLoading(true);
      const response = await economicNexusApi.exportTaxFiling(year, quarter);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Economic_Nexus_Tax_Filing_${year}_Q${quarter || "All"}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Tax filing report exported successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export tax filing report");
    } finally {
      setExportLoading(false);
    }
  }, [year, quarter]);

  // Format values using memoized data
  const formattedSales = useMemo(() => {
    return parseFloat(nexusData?.totalSales || 0).toFixed(2);
  }, [nexusData?.totalSales]);

  const formattedTax = useMemo(() => {
    return parseFloat(nexusData?.totalTaxCollected || 0).toFixed(2);
  }, [nexusData?.totalTaxCollected]);

  return (
    <div className={styles.economyNexusContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Economy Nexus</h3>
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <span>⚠️</span>
          <span>{error?.message || "Failed to load economy nexus data"}</span>
        </div>
      )}

      <div className={styles.filtersSection}>
        <YearSelector value={year} onChange={handleYearChange} />

        <div className={styles.filterGroup}>
          <Form.Label className={styles.label}>Select Quarter</Form.Label>
          <Form.Control
            as="select"
            value={quarter}
            onChange={handleQuarterChange}
            className={styles.select}
          >
            {quarterOptions}
          </Form.Control>
        </div>

        <div className={styles.exportAction}>
          <Button
            variant="primary"
            className={styles.exportButton}
            onClick={handleExport}
            disabled={exportLoading || isLoading || quarter === 0}
          >
            {exportLoading ? (
              "Exporting..."
            ) : (
              <>
                <DocumentDownload size="20" className="me-2" />
                Export
              </>
            )}
          </Button>
        </div>
      </div>

      <div className={styles.contentSection}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner} />
            <span className={styles.loadingText}>Loading data...</span>
          </div>
        ) : quarter === 0 ? (
          <div className={styles.infoNote}>
            ℹ️ Please select a quarter to view the nexus data breakdown.
          </div>
        ) : (
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>💰</div>
              <div className={styles.statInfo}>
                <label className={styles.statLabel}>Total Sales</label>
                <div className={styles.statValue}>${formattedSales}</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>📊</div>
              <div className={styles.statInfo}>
                <label className={styles.statLabel}>Total Transactions</label>
                <div className={styles.statValue}>{nexusData?.totalTransactions || 0}</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>🏦</div>
              <div className={styles.statInfo}>
                <label className={styles.statLabel}>Total Tax Collected</label>
                <div className={styles.statValue}>${formattedTax}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

EconomyNexus.displayName = "EconomyNexus";

export default EconomyNexus;
